import { isNative, getPlatform, isAndroid, isIos } from 'src/utils/platform';
import { env } from 'src/config/env';
import {
  CustomerInfo,
  LOG_LEVEL,
  PURCHASES_ERROR_CODE,
  Purchases,
  PurchasesEntitlementInfo,
  PurchasesPackage,
} from '@revenuecat/purchases-capacitor';
import { RevenueCatUI, PAYWALL_RESULT, type PaywallListener } from '@revenuecat/purchases-capacitor-ui';
import { getAnalyticsDistinctId, logAnalyticsEvent, trackPaywallError } from 'src/modules/analytics/analytics';
import {
  ANNUAL_ENTITLEMENT_ID,
  ANNUAL_ENTITLEMENT_ID_WITHOUT_TRIAL,
  ANNUAL_INSTALLMENTS_ENTITLEMENT_ID,
  MONTHLY_ENTITLEMENT_ID,
  MONTHLY_ENTITLEMENT_ID_WITHOUT_TRIAL,
  RevenueCatPlacement,
  SEMI_ANNUAL_ENTITLEMENT_ID,
} from 'src/modules/purchase/purchase.constants';
import { reportError } from 'src/modules/logs/Sentry';
import { isDebugMode } from 'src/contexts/DebugContext';
import { getModuleExternalId } from 'src/utils/external-id';
import { PurchasesOfferings } from '@revenuecat/purchases-typescript-internal-esm';
import { getAppsFlyerUID } from 'src/modules/AppsFlyer/AppsFlyer.module';
import { isWebWithRcSdk } from 'src/modules/purchase/purchase.env';

export { PAYWALL_RESULT };

const loadWebModule = () => import('src/modules/purchase/RevenueCatWeb');

const readErrorField = (error: unknown, key: string): unknown =>
  typeof error === 'object' && error !== null ? Reflect.get(error, key) : undefined;

export const getErrorMessage = (error: unknown): string | undefined => {
  const message = readErrorField(error, 'message');
  return typeof message === 'string' ? message : undefined;
};

export const getErrorCodeName = (error: unknown): string => {
  const code = readErrorField(error, 'code');
  if (!code && code !== 0) return 'UNKNOWN';
  const entry = Object.entries(PURCHASES_ERROR_CODE).find(
    ([, value]) => value === code,
  );
  return entry?.[0] ?? `CODE_${code}`;
};

export const initInAppPurchase = async () => {
  if (isWebWithRcSdk()) {
    const { Purchases: PurchasesWeb } = await import('@revenuecat/purchases-js');
    const { initWebPurchases } = await loadWebModule();
    const anonId = PurchasesWeb.generateRevenueCatAnonymousAppUserId();
    initWebPurchases(env.rcWebApiKey, anonId);
    return;
  }

  if (getPlatform() === 'web') {
    await Purchases.setMockWebResults({ shouldMockWebResults: true });
  }

  await Purchases.setLogLevel({
    level: env.mode === 'production' ? LOG_LEVEL.WARN : LOG_LEVEL.DEBUG,
  });
  if (isIos()) {
    await Purchases.configure({
      apiKey: 'appl_gfNrmAOtFfJjCadVHLxaSfxjzOj',
    });
    await Purchases.collectDeviceIdentifiers();
  }
  if (isAndroid()) {
    await Purchases.configure({
      apiKey: 'goog_QXvQYkNfgDgpdvAieZIVpYgbRhz',
    });
  }

};

export const presentWebPaywall = async (params: {
  placement?: RevenueCatPlacement;
  htmlTarget?: HTMLElement;
  customerEmail?: string;
  selectedLocale?: string;
  onBack?: (closePaywall: () => void) => void;
  onPurchaseCancelled?: () => void;
  onPurchaseError?: (error: Error, invalidEmail: boolean) => void;
}): Promise<{
  productIdentifier: string;
  customerInfo: CustomerInfo | null;
} | null> => {
  if (!isWebWithRcSdk()) return null;
  const { presentWebPaywall: present } = await loadWebModule();
  return present(params);
};

export type IdentityUser = { id: number; family_id: number };
export type IdentityTrigger = 'boot' | 'signup' | 'login' | 'paywall_guard';
type IdentityOutcome = 'noop' | 'identified' | 'switched' | 'failed';

const RC_ANONYMOUS_PREFIX = '$RCAnonymousID:';

const identityOutcomeFrom = (previousAppUserId: string, expectedAppUserId: string): IdentityOutcome => {
  if (previousAppUserId === expectedAppUserId) return 'noop';
  if (previousAppUserId.startsWith(RC_ANONYMOUS_PREFIX)) return 'identified';
  return 'switched';
};

const trackIdentityResolved = (props: {
  identity_outcome: IdentityOutcome;
  identity_trigger: IdentityTrigger;
  is_rc_customer_created: boolean | null;
  error_code?: string;
}) => {
  try {
    Promise.resolve(logAnalyticsEvent('revenuecat_identity_resolved', props)).catch((error) => {
      reportError('Failed to track RevenueCat identity', error);
    });
  } catch (error) {
    reportError('Failed to track RevenueCat identity', error);
  }
};

export const logInAppPurchase = async (
  user: IdentityUser,
  trigger: IdentityTrigger,
): Promise<CustomerInfo | undefined> => {
  if (isWebWithRcSdk()) {
    const { changeWebUser, setWebAttributes, mapWebCustomerInfoToCapacitor } = await loadWebModule();
    const webInfo = await changeWebUser(getModuleExternalId(user.family_id));
    await setWebAttributes({
      userID: user.id.toString(),
      familyID: user.family_id.toString(),
      $posthogUserId: getAnalyticsDistinctId() ?? '',
      isFakeUser: (Boolean(env.isCi) || isDebugMode()).toString(),
      isStaging: (!env.isProd).toString(),
      isProd: Boolean(env.isProd).toString(),
    });
    return mapWebCustomerInfoToCapacitor(webInfo);
  }

  const expectedAppUserId = getModuleExternalId(user.family_id);
  let customerInfo: CustomerInfo;
  try {
    // Read before logIn: the SDK swaps the id on success, so the previous id is the only way to
    // tell a no-op from a switch.
    const { appUserID: previousAppUserId } = await Purchases.getAppUserID();
    const result = await Purchases.logIn({ appUserID: expectedAppUserId });
    customerInfo = result.customerInfo;
    trackIdentityResolved({
      identity_outcome: identityOutcomeFrom(previousAppUserId, expectedAppUserId),
      identity_trigger: trigger,
      is_rc_customer_created: result.created ?? null,
    });
  } catch (error) {
    trackIdentityResolved({
      identity_outcome: 'failed',
      identity_trigger: trigger,
      is_rc_customer_created: null,
      error_code: getErrorCodeName(error),
    });
    throw error;
  }

  await Purchases.setAttributes({
    userID: user.id.toString(),
    familyID: user.family_id.toString(),
    $posthogUserId: getAnalyticsDistinctId() ?? '',
    $appsflyerId: await getAppsFlyerUID(),
    isFakeUser: (Boolean(env.isCi) || isDebugMode()).toString(),
    isStaging: (!env.isProd).toString(),
    isProd: Boolean(env.isProd).toString(),
  });
  await Purchases.invalidateCustomerInfoCache();

  return customerInfo;
};

export class RevenueCatIdentityError extends Error {
  readonly name = 'RevenueCatIdentityError';

  constructor(
    readonly expectedAppUserId: string,
    readonly actualAppUserId: string,
  ) {
    super(`RevenueCat identity mismatch: expected ${expectedAppUserId}, got ${actualAppUserId}`);
  }
}

const readAppUserId = async () => (await Purchases.getAppUserID()).appUserID;

const readAppUserIdOrTrack = async (
  user: IdentityUser,
  placement: RevenueCatPlacement | undefined,
): Promise<string> => {
  try {
    return await readAppUserId();
  } catch (error) {
    trackPaywallError({
      operation: 'identity',
      error_code: getErrorCodeName(error),
      error_message: getErrorMessage(error),
      placement,
      family_id: user.family_id,
    });
    throw error;
  }
};

export const ensureInAppPurchaseIdentity = async (
  user: IdentityUser,
  placement?: RevenueCatPlacement,
): Promise<void> => {
  if (!isNative()) {
    return;
  }
  const expectedAppUserId = getModuleExternalId(user.family_id);
  if ((await readAppUserIdOrTrack(user, placement)) === expectedAppUserId) {
    return;
  }

  try {
    await logInAppPurchase(user, 'paywall_guard');
  } catch (error) {
    trackPaywallError({
      operation: 'identity',
      error_code: getErrorCodeName(error),
      error_message: getErrorMessage(error),
      placement,
      family_id: user.family_id,
    });
    throw error;
  }

  const actualAppUserId = await readAppUserIdOrTrack(user, placement);
  if (actualAppUserId === expectedAppUserId) {
    return;
  }
  const error = new RevenueCatIdentityError(expectedAppUserId, actualAppUserId);
  trackPaywallError({
    operation: 'identity',
    error_code: 'IDENTITY_MISMATCH',
    error_message: error.message,
    placement,
    family_id: user.family_id,
  });
  throw error;
};

export const getActiveSubscription = (
  customerInfo?: CustomerInfo,
): PurchasesEntitlementInfo | undefined => {
  if (!customerInfo) {
    return undefined;
  }
  if (customerInfo.entitlements.active[MONTHLY_ENTITLEMENT_ID]) {
    return customerInfo.entitlements.active[MONTHLY_ENTITLEMENT_ID];
  }
  if (customerInfo.entitlements.active[MONTHLY_ENTITLEMENT_ID_WITHOUT_TRIAL]) {
    return customerInfo.entitlements.active[
      MONTHLY_ENTITLEMENT_ID_WITHOUT_TRIAL
    ];
  }
  if (customerInfo.entitlements.active[ANNUAL_ENTITLEMENT_ID]) {
    return customerInfo.entitlements.active[ANNUAL_ENTITLEMENT_ID];
  }
  if (customerInfo.entitlements.active[ANNUAL_ENTITLEMENT_ID_WITHOUT_TRIAL]) {
    return customerInfo.entitlements.active[
      ANNUAL_ENTITLEMENT_ID_WITHOUT_TRIAL
    ];
  }
  if (customerInfo.entitlements.active[ANNUAL_INSTALLMENTS_ENTITLEMENT_ID]) {
    return customerInfo.entitlements.active[
      ANNUAL_INSTALLMENTS_ENTITLEMENT_ID
    ];
  }
  if (customerInfo.entitlements.active[SEMI_ANNUAL_ENTITLEMENT_ID]) {
    return customerInfo.entitlements.active[SEMI_ANNUAL_ENTITLEMENT_ID];
  }
  return undefined;
};

export const getPurchasesPackages = async (
  placement?: RevenueCatPlacement,
): Promise<PurchasesPackage[]> => {
  if (isWebWithRcSdk()) {
    const { getWebOfferings, mapWebPackageToCapacitor } = await loadWebModule();
    const { packages, offeringIdentifier } = await getWebOfferings(placement);
    return packages.map(pkg => mapWebPackageToCapacitor(pkg, offeringIdentifier));
  }

  const offering = await getOffering(placement);
  if (offering) {
    return offering.availablePackages;
  }
  return [];
};
export const getOffering = async (placement?: RevenueCatPlacement) => {
  if (placement) {
    const offering = await Purchases.getCurrentOfferingForPlacement({
      placementIdentifier: placement,
    });
    if (offering) {
      return offering;
    }
  }
  return (await Purchases.getOfferings()).current;
};

export const getAnnualAndSemiAnnualPackages = async (
  withTrial: boolean,
): Promise<PurchasesPackage[]> => {
  const offerings = await getOfferings();
  const annualOffering = withTrial
    ? offerings.all['premium_annual']
    : offerings.all['premium_annual_without_trial'];
  const semiAnnualOffering = offerings.all['premium_semi_annual'];

  const packages: PurchasesPackage[] = [];
  if (annualOffering?.annual) {
    packages.push(annualOffering.annual);
  } else {
    console.warn('getAnnualAndSemiAnnualPackages: annual offering not found');
    reportError('getAnnualAndSemiAnnualPackages: annual offering not found');
  }
  if (semiAnnualOffering?.sixMonth) {
    packages.push(semiAnnualOffering.sixMonth);
  } else {
    console.warn('getAnnualAndSemiAnnualPackages: semi-annual offering not found');
    reportError('getAnnualAndSemiAnnualPackages: semi-annual offering not found');
  }
  return packages;
};

export const getOfferings = async (): Promise<PurchasesOfferings> => {
  if (isWebWithRcSdk()) {
    const { getWebOfferingsAll } = await loadWebModule();
    return getWebOfferingsAll();
  }

  return Purchases.getOfferings();
};

export const syncPurchasesCompat = async (): Promise<void> => {
  if (isWebWithRcSdk()) return;
  await Purchases.syncPurchases();
};

export const getCustomerInfoCompat = async (): Promise<{ customerInfo: CustomerInfo }> => {
  if (isWebWithRcSdk()) {
    const { getWebCustomerInfo, mapWebCustomerInfoToCapacitor } = await loadWebModule();
    const webInfo = await getWebCustomerInfo();
    return { customerInfo: mapWebCustomerInfoToCapacitor(webInfo) };
  }
  return Purchases.getCustomerInfo();
};

export const presentNativePaywall = async (options: {
  user: IdentityUser;
  placement?: RevenueCatPlacement;
  displayCloseButton?: boolean;
  listener?: PaywallListener;
}): Promise<PAYWALL_RESULT> => {
  await ensureInAppPurchaseIdentity(options.user, options.placement);
  const offering = await getOffering(options.placement);
  let purchaseCompleted = false;
  const { result } = await RevenueCatUI.presentPaywall({
    offering: offering ?? undefined,
    displayCloseButton: options.displayCloseButton ?? true,
    customVariables: {
      platform: isIos() ? 'Apple' : 'Google',
    },
    listener: {
      ...options.listener,
      onPurchaseCompleted: info => {
        purchaseCompleted = true;
        options.listener?.onPurchaseCompleted?.(info);
      },
    },
  });
  logAnalyticsEvent('paywall_result_received', {
    placement: options.placement ?? 'none',
    result,
    purchase_completed: purchaseCompleted,
  });
  // The native paywall can report CANCELLED after a completed purchase.
  if (
    purchaseCompleted &&
    result !== PAYWALL_RESULT.PURCHASED &&
    result !== PAYWALL_RESULT.RESTORED
  ) {
    return PAYWALL_RESULT.PURCHASED;
  }
  return result;
};

export const presentCustomerCenter = async (user: IdentityUser): Promise<void> => {
  await ensureInAppPurchaseIdentity(user);
  await RevenueCatUI.presentCustomerCenter();
};
